import classNames from 'classnames';
import type { KeyboardEventHandler, LegacyRef, Ref } from 'react';
import { LinkInfo } from '../../types';
import IconRenderer, { IconDefinition } from '../IconRenderer';
import LinkInfoRenderer from '../LinkInfoRenderer';
import styles from './styles/styles.module.scss';

export type TabDefinition = {
  id: string;
  icon?: IconDefinition<'orientation'>;
  label: string;
  linkInfo?: LinkInfo;
  role?: string;
};

type TabProps = TabDefinition & {
  active: boolean;
  indicatorPosition?: 'top' | 'bottom';
  onClick: (id: TabDefinition['id']) => void;
  onKeyDown: KeyboardEventHandler<HTMLElement>;
  tabRef: (el: HTMLElement) => void;
};

const Tab = ({ active, id, icon, indicatorPosition = 'bottom', label, linkInfo, onClick, onKeyDown, tabRef }: TabProps) => {
  const content = (
    <span className={styles.tabContent}>
      {icon && <IconRenderer icon={icon} defaults={{ color: 'currentColor', size: 'sm' }} />}
      <span>{label}</span>
    </span>
  );

  const onTabClick = () => {
    onClick(id);
  };

  const classes = classNames(styles.tab, active && styles.active, { [styles.topIndicator]: indicatorPosition === 'top' });
  const tabIndex = active ? 0 : -1;

  if (linkInfo) {
    return (
      <LinkInfoRenderer
        className={classes}
        linkInfo={linkInfo}
        onClick={onTabClick}
        onKeyDown={onKeyDown}
        ref={tabRef as Ref<HTMLAnchorElement>}
        tabIndex={tabIndex}
      >
        {content}
      </LinkInfoRenderer>
    );
  }
  return (
    <button
      aria-controls={`tabpanel-${id}`}
      aria-selected={active}
      className={classes}
      id={`tab-${id}`}
      onClick={onTabClick}
      onKeyDown={onKeyDown}
      ref={tabRef as LegacyRef<HTMLButtonElement>}
      role="tab"
      tabIndex={tabIndex}
      type="button"
    >
      {content}
    </button>
  );
};

export default Tab;
